/**
 * Botón base (design-system §7 · Botón).
 *
 * - `primary`: coral 500 sólido, **solo** con etiqueta grande (≥18.66 px bold) → tamaño `lg`.
 * - `compact`: coral 700 con texto blanco 14 px/600. Es el CTA habitual del Admin.
 * - `secondary`: contorno marino. `ghost`: terciario. `destructive`: error 600.
 *
 * Foco: anillo coral 2 px con offset 2 px. Estado `loading` reemplaza la etiqueta por un texto de
 * progreso (nunca un spinner solo) y bloquea el botón. Sin colores fuera de los tokens `--fm-*`.
 */
import { Slot } from '@radix-ui/react-slot';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

export type ButtonVariant = 'primary' | 'compact' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-fm-2 rounded-fm-md font-ui select-none ' +
  'transition-colors duration-fm-fast ease-fm-standard ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus ' +
  'disabled:opacity-45 disabled:shadow-none disabled:pointer-events-none ' +
  'motion-reduce:transition-none';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // Coral 500 sólido: exige etiqueta grande y bold para cumplir AA (design-system §3.3).
  primary: 'bg-fm-primary text-fm-on-primary hover:bg-fm-primary-hover font-display font-bold',
  compact: 'bg-fm-primary-compact text-fm-on-primary hover:brightness-95 font-semibold',
  secondary:
    'bg-transparent text-fm-text border border-fm-text hover:bg-fm-bg-sunken font-semibold',
  ghost: 'bg-transparent text-fm-text hover:bg-fm-bg-sunken font-medium',
  destructive: 'bg-fm-danger text-fm-text-inverse hover:brightness-95 font-semibold',
};

/**
 * `md` (44 px) es el objetivo táctil mínimo del design system §10; `sm` (40 px) queda para barras
 * de filtros y acciones densas del Admin (§10 admite ≥32 px con separación ≥8 px).
 */
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-fm-control-sm px-fm-3 text-fm-sm',
  md: 'h-fm-touch px-fm-4 text-fm-sm',
  lg: 'h-fm-control-lg px-fm-6 text-fm-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Muestra el texto de progreso y deshabilita el botón. */
  loading?: boolean;
  /** Texto de progreso; por defecto `common.states.processing`. */
  loadingText?: string;
  /** Icono Lucide de 20 px a la izquierda (se oculta mientras carga). */
  icon?: ReactNode;
  /** Renderiza el hijo como elemento raíz (para `<Link>` de TanStack Router). */
  asChild?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'compact',
  size,
  loading = false,
  loadingText,
  icon,
  asChild = false,
  className,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  const resolvedSize: ButtonSize = size ?? (variant === 'primary' ? 'lg' : 'md');
  const Component = asChild ? Slot : 'button';

  // `Slot` exige un único hijo elemento: con `asChild` se delega el contenido al hijo.
  const content = asChild ? (
    children
  ) : loading ? (
    <span>{loadingText ?? t('common.states.processing')}</span>
  ) : (
    <>
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      {children}
    </>
  );

  return (
    <Component
      type={asChild ? undefined : (type ?? 'button')}
      className={cn(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[resolvedSize], className)}
      disabled={asChild ? undefined : (disabled ?? loading)}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Component>
  );
}
